// Catálogo de tutoriales: validación y consultas puras. El catálogo es un JSON publicado junto a los videos
// (S3 + CloudFront en producción, `public/tutoriales/` en desarrollo). Todo lo que no tenga la forma esperada se
// descarta en vez de romper la pantalla.

const esTexto = (v) => typeof v === 'string' && v.trim() !== '';

/**
 * @returns {{grupos: Array<{id,titulo,orden}>, tutoriales: Array<object>}} solo con entradas bien formadas.
 * Un tutorial exige id, título, video y un grupo que exista.
 */
export function normalizarCatalogo(datos) {
  const vacio = { grupos: [], tutoriales: [] };
  if (!datos || typeof datos !== 'object' || Array.isArray(datos)) return vacio;

  const grupos = (Array.isArray(datos.grupos) ? datos.grupos : [])
    .filter((g) => g && esTexto(g.id) && esTexto(g.titulo))
    .map((g) => ({ id: g.id, titulo: g.titulo, orden: Number.isFinite(g.orden) ? g.orden : 999 }));
  const idsGrupos = new Set(grupos.map((g) => g.id));

  const tutoriales = (Array.isArray(datos.tutoriales) ? datos.tutoriales : [])
    .filter(
      (t) => t && esTexto(t.id) && esTexto(t.titulo) && esTexto(t.video) && idsGrupos.has(t.grupo)
    )
    .map((t) => ({
      id: t.id,
      grupo: t.grupo,
      titulo: t.titulo,
      resumen: typeof t.resumen === 'string' ? t.resumen : '',
      duracionS: Number.isFinite(t.duracionS) ? t.duracionS : null,
      video: t.video,
      poster: esTexto(t.poster) ? t.poster : null,
      capitulos: esTexto(t.capitulos) ? t.capitulos : null,
      rutas: Array.isArray(t.rutas) ? t.rutas.filter(esTexto) : [],
      actualizado: esTexto(t.actualizado) ? t.actualizado : null,
    }));

  return { grupos, tutoriales };
}

const comparar = (a, b) => a.localeCompare(b, 'es');

/** Grupos ordenados por `orden`, solo los que tienen tutoriales, con sus tutoriales ordenados por título. */
export function agruparTutoriales(catalogo) {
  return [...catalogo.grupos]
    .sort((a, b) => a.orden - b.orden || comparar(a.titulo, b.titulo))
    .map((g) => ({
      ...g,
      tutoriales: catalogo.tutoriales
        .filter((t) => t.grupo === g.id)
        .sort((a, b) => comparar(a.titulo, b.titulo)),
    }))
    .filter((g) => g.tutoriales.length > 0);
}

const sinTildes = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Igual que `agruparTutoriales`, pero solo con los tutoriales cuyo título o resumen contienen el texto. */
export function buscarTutoriales(catalogo, texto) {
  const q = sinTildes(String(texto ?? '').trim());
  if (!q) return agruparTutoriales(catalogo);
  return agruparTutoriales({
    ...catalogo,
    tutoriales: catalogo.tutoriales.filter((t) =>
      sinTildes(`${t.titulo} ${t.resumen}`).includes(q)
    ),
  });
}

/** `/admin/odt/:id/gantt` coincide con `/admin/odt/245/gantt` (con o sin barra final). Los `:x` son un segmento. */
export function coincideRuta(patron, pathname) {
  const limpiar = (s) => s.replace(/\/+$/, '');
  const a = limpiar(patron).split('/');
  const b = limpiar(pathname).split('/');
  return a.length === b.length && a.every((seg, i) => seg.startsWith(':') || seg === b[i]);
}

/** Id del primer tutorial cuyas `rutas` coinciden con la pantalla actual, o `null`. */
export function tutorialParaRuta(catalogo, pathname) {
  const t = catalogo.tutoriales.find((x) => x.rutas.some((r) => coincideRuta(r, pathname)));
  return t ? t.id : null;
}

/** Une la base del contenido con una ruta relativa. Lo ya absoluto se respeta; lo vacío da `null`. */
export function urlContenido(base, relativa) {
  if (!relativa) return null;
  if (/^https?:\/\//i.test(relativa)) return relativa;
  return `${String(base).replace(/\/+$/, '')}/${relativa.replace(/^\/+/, '')}`;
}

const DIAS_NUEVO = 30;

/** Un tutorial es "nuevo" durante 30 días desde su fecha `actualizado` (`YYYY-MM-DD`). */
export function esNuevo(tutorial, hoy) {
  if (!tutorial.actualizado) return false;
  const dias = (Date.parse(hoy) - Date.parse(tutorial.actualizado)) / 86400000;
  return dias >= 0 && dias <= DIAS_NUEVO;
}
